import React from "react";
import { Box, Typography } from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import { motion } from "framer-motion";
import { colors } from "./colors";

const MotionBox = motion(Box);

const SECTION_SX = {
  position: "relative",
  zIndex: 5,
  marginTop: {
    xs: "-40px",
    md: "-70px",
  },
  paddingTop: {
    xs: "5rem",
    md: "7rem",
  },
  paddingBottom: {
    xs: "5rem",
    md: "7rem",
  },
  background: `
    linear-gradient(
      to bottom,
      rgba(8,8,8,0) 0%,
      #080808 24%
    )
  `,
};

const ROW_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "72rem",
  mx: "auto",
  display: { xs: "grid", md: "flex" },
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
  },
  flexDirection: { md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  justifyContent: { md: "center" },
  gap: { xs: 2, md: 0 },
};

const CARD_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  px: { xs: 1, md: 1.5 },
  py: { xs: "1.25rem", md: "1.5rem" },
  position: "relative",
  background: "transparent",
  backdropFilter: "blur(12px)",
  flex: { md: 1 },
  minWidth: 0,
  overflow: "hidden",
  transition: "transform 280ms ease",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "2.75rem",
    height: "1.5px",
    borderRadius: "2px",
    background: "linear-gradient(90deg, transparent, rgba(232,163,61,0.55), transparent)",
    transition: "width 320ms ease, opacity 320ms ease",
    opacity: 0.7,
  },
  "&:hover": {
    transform: "translateY(-2px)",
    "&::after": {
      width: "3.5rem",
      opacity: 1,
    },
  },
};

const SEPARATOR_SX = {
  position: "relative",
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  justifyContent: "center",
  width: "1px",
  overflow: "visible",
  pointerEvents: "none",
  flexShrink: 0,
  mx: { md: 0.5 },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "28%",
    background: "linear-gradient(to bottom, transparent, rgba(8,8,8,0.9), transparent)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "28%",
    background: "linear-gradient(to top, transparent, rgba(8,8,8,0.9), transparent)",
    pointerEvents: "none",
  },
};

const ICON_WRAP_SX = {
  color: colors.primary.main,
  mb: 1,
  filter: "drop-shadow(0 0 12px rgba(232,163,61,0.22))",
};

const TITLE_SX = {
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.9)",
};

const TEXT_SX = {
  mt: 0.5,
  fontSize: { xs: "0.72rem", md: "0.76rem" },
  lineHeight: 1.55,
  fontWeight: 500,
  color: colors.text.secondary,
};

const BENEFITS = [
  {
    key: "identity",
    icon: VerifiedUserOutlinedIcon,
    title: "Human Reviewed",
    text: "No smurfs. No throwaways.",
  },
  {
    key: "reputation",
    icon: GpsFixedOutlinedIcon,
    title: "Persistent Reputation",
    text: "Your behavior follows you.",
  },
  {
    key: "anticheat",
    icon: ShieldOutlinedIcon,
    title: "Advanced Anti-Cheat",
    text: "Server-side. Always watching.",
  },
  {
    key: "team",
    icon: Groups2OutlinedIcon,
    title: "Team Focused",
    text: "Impact over K/D.",
  },
];

function BenefitSeparator() {
  return (
    <Box sx={SEPARATOR_SX}>
      <Box
        sx={{
          position: "relative",
          width: "1px",
          height: "120px",
          background: `
            linear-gradient(
              to bottom,
              transparent 0%,
              rgba(255,255,255,0.05) 18%,
              rgba(232,163,61,0.16) 50%,
              rgba(255,255,255,0.05) 82%,
              transparent 100%
            )
          `,
        }}
      >
        <MotionBox
          aria-hidden
          animate={{
            opacity: [0.35, 0.7, 0.35],
            scaleY: [1, 1.12, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: "8px",
            height: "56px",
            borderRadius: "999px",
            background: `
              radial-gradient(
                ellipse at center,
                rgba(232,163,61,0.34),
                transparent 72%
              )
            `,
            filter: "blur(10px)",
          }}
        />
      </Box>
    </Box>
  );
}

export default function HeroBenefits() {
  return (
    <Box sx={SECTION_SX}>
      <Box sx={ROW_SX}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(
                ellipse at center,
                rgba(232,163,61,0.06),
                transparent 72%
              )
            `,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        {BENEFITS.map((item, index) => {
          const Icon = item.icon;
          return (
            <React.Fragment key={item.key}>
              <MotionBox
                initial={{
                  opacity: 0,
                  y: 28,
                  filter: "blur(10px)",
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  filter: "blur(0px)",
                }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{
                  duration: 0.9,
                  delay: index * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                sx={CARD_SX}
              >
                <Box sx={ICON_WRAP_SX}>
                  <Icon sx={{ fontSize: { xs: "3.75rem", md: "4rem" } }} />
                </Box>
                <Typography sx={TITLE_SX}>{item.title}</Typography>
                <Typography sx={TEXT_SX}>{item.text}</Typography>
              </MotionBox>
              {index < BENEFITS.length - 1 && <BenefitSeparator />}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
